import { selectActiveLoyaltyProvider } from "../loyalty/selectors";
import type { PartnerDirectory, ProviderRef } from "../promotions/partners/port";
import type { DiscountOffer, Sale } from "../promotions/types";

/**
 * 🔴 **The ONLY file in `packages/core/discounts` that names a redux key.** Every rule module
 * takes its inputs as arguments and every hook reads through these, so a store reshape is a
 * change here and nowhere else — and the rules stay testable without a store.
 *
 * Plain selectors taking the whole state, so the same function serves `useSelector` in a
 * component and `store.getState()` in the adapter. Nothing is read at module-eval time (audit
 * checks H/J).
 */

/** The sale being discounted. */
export const selectSaleData = (state: any): Sale => state.saleDataStore?.saleData;

/**
 * A stable reference for "the catalog has no discounts".
 *
 * 🔴 Module-level, not a `?? {}` literal: web's `arrayToMap` stores `null` for an empty list, and
 * a fresh object per call makes `useSelector`'s reference compare report a change on EVERY store
 * action — re-rendering whatever reads this, the web cart among them since 2026-09-24
 * (`useCustomerRewards`). Same hazard, same fix, as `customerSummary/selectors`' `EMPTY`.
 */
const NO_CATALOG_DISCOUNTS: Record<string, DiscountOffer> = {};

/** The catalog's discounts, keyed by id. */
export const selectCatalogDiscounts = (state: any): Record<string, DiscountOffer> =>
  state.catalogStore?.discounts ?? NO_CATALOG_DISCOUNTS;

/**
 * The coupon API's last answer — the expanded discounts for a typed code, its `couponCode`, the
 * provider it attributed the code to, and a `requestId`.
 *
 * ⚠️ Read by `buildEntry` and by both partner request builders. It is a SINGLE slot, not
 * a per-code map, so it describes whichever code was typed most recently.
 */
export const selectAppliedDiscountInfo = (state: any): Record<string, any> =>
  state.discountStore?.discountInfo ?? {};

/**
 * The partner reference id from the current coupon conversation.
 *
 * ⚠️ Lives on the LOYALTY slice (`externalRefId`), because the partner endpoints it belongs to
 * are the loyalty ones. It is a single slot shared by every partner, which is why
 * `referenceIdSource` exists — writing one from a leg that does not issue it overwrites a good
 * id with `undefined`.
 */
export const selectExternalReferenceId = (state: any): string | undefined =>
  state.LoyaltyStore?.externalRefId;

/** Every configured loyalty/coupon provider. */
export const selectCouponProviders = (state: any): any[] =>
  state.businessStore?.businessData?.loyaltyProviders ?? [];

/**
 * The active provider, or `null`.
 *
 * Re-exported from `packages/core/loyalty/selectors` rather than re-derived: it filters by
 * branch and channel and prefers `isPrimary`, and two copies of that would drift. One definition.
 */
export const selectActiveCouponProvider = selectActiveLoyaltyProvider;

/** The partners as the partner port takes them: every configured one, and the active one (the session and sale effects v2). */
export const selectPartnerDirectory = (state: any): PartnerDirectory => ({
  providers: selectCouponProviders(state) as ProviderRef[],
  active: (selectActiveLoyaltyProvider(state) ?? undefined) as ProviderRef | undefined,
});

/** The merchant's business record — the count limits and the authentication switch live on it. */
export const selectBusinessData = (state: any): Record<string, any> =>
  state.businessStore?.businessData ?? {};

export const selectIsOffline = (state: any): boolean => !!state.businessStore?.isOffline;

export const selectCustomerSummary = (state: any): Record<string, any> =>
  state.customerStore?.customerSummary ?? {};

/**
 * The provider record whose `metaId` matches, for resolving a coupon back to its `mid`.
 *
 * ⚠️ The `mid` is what the partner APIs take as `providerId`, and the `metaId` is what a bill
 * discount records. A bill can carry a coupon from a partner that is no longer active, so this
 * lookup — not the active provider — is what the removal path must use.
 */
export const selectProviderByMetaId = (state: any, metaId?: string): any =>
  selectCouponProviders(state).find((provider: any) => provider?.metaId === metaId);
